import { ref, onUnmounted } from "vue"

/**
 * 使用安全的 setTimeout 鏈來防止請求重疊。
 * @param {Function} fn - 要執行的異步函數，回傳 true 時停止輪詢。
 * @param {number} interval - 輪詢間隔時間，預設為 5000 毫秒。
 * @returns {{
 *   isPolling: import('vue').Ref<boolean>,
 *   startPolling: () => void,
 *   stopPolling: () => void
 * }} - 返回響應式狀態和控制函數。
 */
export function usePolling(fn, interval = 5000) {
    const isPolling = ref(false)
    const isExecuting = ref(false)
    let timerId = null

    // 核心執行函數
    const poll = async (...args) => {
        // 防止重疊執行
        if (isExecuting.value) return

        isExecuting.value = true
        const shouldStop = await fn(...args)
        isExecuting.value = false

        if (shouldStop || !isPolling.value) {
            stopPolling()
            return
        }

        timerId = setTimeout(() => poll(...args), interval)
    }

    // 開始輪詢
    const startPolling = (...args) => {
        // 已經在輪詢中
        if (isPolling.value) return

        isPolling.value = true
        poll(...args)
    }

    // 停止輪詢
    const stopPolling = () => {
        isPolling.value = false
        if (timerId) {
            clearTimeout(timerId)
            timerId = null
        }
    }

    // 當組件卸載時，自動停止輪詢以防止內存洩漏
    onUnmounted(stopPolling)

    return {
        isPolling,
        startPolling,
        stopPolling,
    }
}
